import { Icon } from '@iconify/react'
import { useCallback, useMemo, useState } from 'react'
import { HorizontalScrollWithArrows } from '@/components/HorizontalScrollWithArrows'
import { PhoneWhatsAppLink } from '@/components/PhoneWhatsAppLink'
import { Select } from '@/components/Select'
import { useAuth } from '@/hooks/useAuth'
import { ApiClientError } from '@/lib/api/client'
import { fetchCampaignNumberRanking } from '@/lib/campaign-reports'
import { formatCurrency, formatDateTime } from '@/lib/campaign-dashboard'
import type {
  CampaignNumberRankingMode,
  CampaignNumberRankingPayload,
} from '@/types/campaign-report'
import styles from './WinnerRankingPanel.module.css'

interface WinnerRankingPanelProps {
  campaignId: number
}

const MODE_OPTIONS: { value: CampaignNumberRankingMode; label: string }[] = [
  { value: 'min_number', label: 'Menor número' },
  { value: 'max_number', label: 'Maior número' },
  { value: 'top_buyers', label: 'Top compradores' },
]

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function defaultPeriod(): { from: string; to: string } {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0)
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 0, 0)
  return { from: toDatetimeLocalValue(start), to: toDatetimeLocalValue(end) }
}

export function WinnerRankingPanel({ campaignId }: WinnerRankingPanelProps) {
  const { token } = useAuth()
  const defaults = useMemo(() => defaultPeriod(), [])
  const [mode, setMode] = useState<CampaignNumberRankingMode>('min_number')
  const [datetimeFrom, setDatetimeFrom] = useState(defaults.from)
  const [datetimeTo, setDatetimeTo] = useState(defaults.to)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<CampaignNumberRankingPayload | null>(null)

  const handleSearch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const payload = await fetchCampaignNumberRanking(
        campaignId,
        {
          mode,
          datetimeFrom,
          datetimeTo,
        },
        token
      )
      setData(payload)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível carregar o ranking.'
      setError(message)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [campaignId, datetimeFrom, datetimeTo, mode, token])

  const rows = data?.rows ?? []

  return (
    <section className={styles.wrap} aria-labelledby="ranking-heading">
      <div className={styles.grid}>
        <div className={styles.filtersCard}>
          <h2 className={styles.cardTitle} id="ranking-heading">
            <Icon icon="mdi:chart-box-outline" aria-hidden />
            Ranking
          </h2>
          <div className={styles.fields}>
            <Select
              id="ranking-mode"
              label="Critério"
              value={mode}
              onChange={(v) => setMode(v as CampaignNumberRankingMode)}
              options={MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              icon="mdi:sort-variant"
            />
            <div className={styles.dateRow}>
              <div className={styles.field}>
                <label htmlFor="ranking-from">De:</label>
                <input
                  id="ranking-from"
                  type="datetime-local"
                  className={styles.input}
                  value={datetimeFrom}
                  onChange={(e) => setDatetimeFrom(e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="ranking-to">Até:</label>
                <input
                  id="ranking-to"
                  type="datetime-local"
                  className={styles.input}
                  value={datetimeTo}
                  onChange={(e) => setDatetimeTo(e.target.value)}
                />
              </div>
            </div>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => void handleSearch()}
              disabled={loading}
            >
              <Icon
                icon={loading ? 'mdi:loading' : 'mdi:magnify'}
                className={loading ? styles.spin : undefined}
                aria-hidden
              />
              {loading ? 'Buscando…' : 'Buscar'}
            </button>
          </div>
        </div>

        <div className={styles.tableCard}>
          <div className={styles.tableHead}>
            <h3 className={styles.tableTitle}>
              <Icon icon="mdi:format-list-numbered" aria-hidden />
              Ranking filtrado
            </h3>
            <span className={styles.badge}>Top 10</span>
          </div>
          {error ? (
            <p className={styles.muted} role="alert">
              {error}
            </p>
          ) : null}
          <p className={styles.scrollHint}>
            <Icon icon="mdi:gesture-swipe-horizontal" aria-hidden />
            Deslize ou use as setas para ver todas as colunas
          </p>
          <div className={styles.tableRegion}>
            <HorizontalScrollWithArrows scrollClassName={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>WhatsApp</th>
                  <th>Campanha</th>
                  <th>Número</th>
                  <th>Data</th>
                  <th>Qtd</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={styles.emptyCell}>
                      {data
                        ? 'Nenhum resultado para os filtros aplicados.'
                        : 'Defina o período e toque em Buscar.'}
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr key={`${row.number}-${row.name}-${i}`}>
                      <td className={styles.cellStrong}>{row.name}</td>
                      <td>
                        <PhoneWhatsAppLink phone={row.phone} contactName={row.name} />
                      </td>
                      <td>
                        <span className={styles.pill}>{row.campaign_title}</span>
                      </td>
                      <td className={styles.mono}>{row.number}</td>
                      <td className={styles.nowrap}>
                        {row.paid_at ? formatDateTime(row.paid_at) : '—'}
                      </td>
                      <td>{row.quantity}</td>
                      <td className={styles.nowrap}>{formatCurrency(row.total_amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </HorizontalScrollWithArrows>
          </div>
        </div>
      </div>
    </section>
  )
}
